import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { formatDateTime, formatRelative } from '../format';
import { PageHead } from '../Layout';
import { Disclosure } from '../components/Disclosure';
import { Icon } from '../components/Icon';
import type {
  ContentProfile,
  DiscoveryCoverage,
  DiscoveryObservation,
  EditorialEvent,
  EditorialWork,
  PublicationWithProfiles,
} from '../../shared/editorial';

type PreviewObservation = DiscoveryObservation & { sourcePreview?: string | null };

export function coverageForProfile(
  rows: DiscoveryCoverage[],
  profileId: number | null,
): DiscoveryCoverage | null {
  if (profileId === null) return null;
  return rows.find((row) => row.profileId === profileId) ?? null;
}

function displayValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number') return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string') return value.replaceAll('_', ' ');
  return JSON.stringify(value);
}

function readableKey(value: string): string {
  const text = value.replace(/([a-z])([A-Z])/g, '$1 $2').replaceAll('_', ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function scoreOf(observation: PreviewObservation): number {
  const value = observation.judgments.potential;
  return typeof value === 'number' && Number.isFinite(value) ? value : -1;
}

function connectionLabel(observation: PreviewObservation): string {
  const relations = observation.judgments.relations;
  if (!Array.isArray(relations)) return 'Related thread';
  const relation = relations.find((item) => {
    if (!item || typeof item !== 'object') return false;
    return (item as { relation?: unknown }).relation !== 'unrelated';
  }) as { relation?: unknown } | undefined;
  if (typeof relation?.relation !== 'string') return 'Related thread';
  if (relation.relation === 'tension') return 'A useful tension';
  if (relation.relation === 'supporting') return 'Supporting thread';
  if (relation.relation === 'continuation') return 'Continuing thought';
  return 'Related thread';
}

function overviewActivitySummary(event: EditorialEvent): string | null {
  switch (event.eventType) {
    case 'discovery.observed':
      return 'Explored a source from the archive.';
    case 'work.enqueued':
      return event.summary.includes('develop_piece') ? 'Queued a piece for editorial development.' : null;
    case 'work.completed':
      return event.summary.includes('develop_piece') ? 'Completed editorial development.' : null;
    case 'piece.created':
      return 'Started a new piece.';
    case 'piece.revised':
      return 'Saved a new piece revision.';
    case 'piece.queued':
      return 'Added a piece to the publication schedule.';
    case 'piece.published':
      return 'Published a piece.';
    case 'research.completed':
      return 'Completed supporting research.';
    case 'lesson.created':
      return 'Added a new editorial preference.';
    case 'lesson.updated':
    case 'lesson.restored':
      return 'Updated an editorial preference.';
    default:
      return null;
  }
}

export default function Discovery() {
  const [publications, setPublications] = useState<PublicationWithProfiles[]>([]);
  const [profiles, setProfiles] = useState<ContentProfile[]>([]);
  const [profileId, setProfileId] = useState<number | null>(null);
  const [observations, setObservations] = useState<PreviewObservation[]>([]);
  const [coverage, setCoverage] = useState<DiscoveryCoverage | null>(null);
  const [work, setWork] = useState<EditorialWork[]>([]);
  const [activity, setActivity] = useState<EditorialEvent[]>([]);
  const [view, setView] = useState<'overview' | 'diagnostics'>('overview');
  const [contextLoading, setContextLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ items: PublicationWithProfiles[] }>('/editorial/publications')
      .then((response) => {
        if (cancelled) return;
        const nextProfiles = response.items.flatMap((publication) => publication.profiles);
        setPublications(response.items);
        setProfiles(nextProfiles);
        setProfileId((current) => current ?? nextProfiles[0]?.id ?? null);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setContextLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    if (!profileId) {
      setObservations([]);
      setCoverage(null);
      setWork([]);
      setActivity([]);
      return;
    }
    const query = `?profileId=${profileId}`;
    const [discoveryResponse, workResponse, activityResponse] = await Promise.all([
      api.get<{ items: PreviewObservation[]; coverage: DiscoveryCoverage[] }>(`/editorial/discovery${query}`),
      api.get<{ items: EditorialWork[] }>(`/editorial/work${query}`),
      api.get<{ items: EditorialEvent[] }>(`/editorial/activity${query}&limit=100`),
    ]);
    setObservations(discoveryResponse.items);
    setCoverage(coverageForProfile(discoveryResponse.coverage, profileId));
    setWork(workResponse.items);
    setActivity(activityResponse.items);
    setError(null);
  }, [profileId]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [load]);

  useEffect(() => {
    if (contextLoading) return;
    setObservations([]);
    setCoverage(null);
    setWork([]);
    setActivity([]);
    if (!profileId) {
      setLoading(false);
      return;
    }
    void refresh();
  }, [contextLoading, profileId, refresh]);

  const pageLoading = contextLoading || loading;

  const currentProfile = profiles.find((profile) => profile.id === profileId) ?? null;
  const activeWork = work.filter((item) => item.status === 'pending' || item.status === 'claimed').length;
  const failures = work.filter((item) => item.status === 'failed').length;
  const progress = coverage && coverage.eligibleSources > 0
    ? Math.min(100, Math.round((coverage.observedSources / coverage.eligibleSources) * 100))
    : 0;
  const strongest = useMemo(
    () => [...observations]
      .filter((observation) => Boolean(observation.sourcePreview?.trim()))
      .sort((a, b) => scoreOf(b) - scoreOf(a) || Date.parse(b.lastExaminedAt) - Date.parse(a.lastExaminedAt))
      .slice(0, 4),
    [observations],
  );
  const overviewActivity = useMemo(
    () => activity
      .map((event) => ({ event, summary: overviewActivitySummary(event) }))
      .filter((item): item is { event: EditorialEvent; summary: string } => Boolean(item.summary))
      .slice(0, 5),
    [activity],
  );
  const workCounts = useMemo(
    () => ['pending', 'claimed', 'done', 'failed'].map((status) => ({
      status,
      count: work.filter((item) => item.status === status).length,
    })),
    [work],
  );

  return (
    <div className="discovery-page">
      <PageHead
        title="Discovery"
        subtitle="A quiet view of how Tentacle is finding and developing material in the archive."
        actions={
          <button className="button-with-icon" onClick={() => void refresh()} disabled={pageLoading || !profileId}>
            <Icon name="refresh" /> Refresh
          </button>
        }
      />

      <div className="discovery-contextbar">
        <label>
          <span>Publication and content type</span>
          <select disabled={contextLoading} value={profileId ?? ''} onChange={(event) => setProfileId(Number(event.target.value) || null)}>
            {contextLoading ? <option value="">Loading content types…</option> : null}
            {!contextLoading && publications.length === 0 ? <option value="">No content types</option> : null}
            {publications.map((publication) => (
              <optgroup key={publication.id} label={publication.name}>
                {publication.profiles.map((profile) => (
                  <option key={profile.id} value={profile.id}>{publication.name} · {profile.name}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>
        <div className="view-switch" aria-label="Discovery view">
          <button aria-pressed={view === 'overview'} onClick={() => setView('overview')}>Overview</button>
          <button aria-pressed={view === 'diagnostics'} onClick={() => setView('diagnostics')}>Diagnostics</button>
        </div>
      </div>

      {error ? <p className="editorial-alert fail" role="alert">Discovery data could not be loaded: {error}</p> : null}
      {pageLoading ? <div className="empty">Loading discovery…</div> : null}

      {!pageLoading && !error && view === 'overview' ? (
        <>
          <section className="discovery-status" aria-labelledby="discovery-status-title">
            <div className="discovery-status-copy">
              <span className="status-orbit" aria-hidden="true"><Icon name="spark" /></span>
              <div>
                <h2 id="discovery-status-title">
                  {coverage?.observedSources
                    ? `${coverage.observedSources.toLocaleString()} source${coverage.observedSources === 1 ? '' : 's'} explored`
                    : 'No discoveries recorded yet'}
                </h2>
                <p>
                  {coverage?.lastExaminedAt
                    ? `Latest recorded observation ${formatRelative(coverage.lastExaminedAt)} for ${currentProfile?.name ?? 'this profile'}.`
                    : `No source has been examined for ${currentProfile?.name ?? 'this content type'} yet.`}
                </p>
              </div>
            </div>
            <div className="coverage-progress" aria-label={`${progress}% of eligible archive sources observed`}>
              <span style={{ width: `${progress}%` }} />
            </div>
            <dl className="discovery-stats">
              <div><dt>Archive observed</dt><dd>{coverage ? `${coverage.observedSources.toLocaleString()} / ${coverage.eligibleSources.toLocaleString()}` : '—'}</dd></div>
              <div><dt>Due for another look</dt><dd>{coverage?.dueForRevisit.toLocaleString() ?? '—'}</dd></div>
              <div><dt>Open work</dt><dd>{activeWork.toLocaleString()}</dd></div>
            </dl>
          </section>

          <section className="discovery-signals" aria-labelledby="discovery-signals-title">
            <div className="section-heading">
              <div>
                <h2 id="discovery-signals-title">Signals worth attention</h2>
                <p>Real fragments from the archive and the threads they connect to.</p>
              </div>
              <span>{observations.length} observed</span>
            </div>
            {strongest.length === 0 ? (
              <div className="empty calm-empty">
                {observations.length > 0
                  ? 'Source previews are not available for the loaded observations.'
                  : 'No observations yet. Useful archive fragments will appear here after they are recorded.'}
              </div>
            ) : (
              <div className="signal-list">
                {strongest.map((observation) => (
                  <article key={observation.id}>
                    <header className="signal-head">
                      <span>{readableKey(observation.sourceType)} #{observation.sourceId}</span>
                      <time title={formatDateTime(observation.lastExaminedAt)}>{formatRelative(observation.lastExaminedAt)}</time>
                    </header>
                    <blockquote>{observation.sourcePreview}</blockquote>
                    {observation.relatedSources[0]?.text ? (
                      <div className="signal-connection">
                        <span>{connectionLabel(observation)}</span>
                        <p>{observation.relatedSources[0].text}</p>
                      </div>
                    ) : null}
                  </article>
                ))}
              </div>
            )}
          </section>

          {overviewActivity.length > 0 ? (
            <section className="discovery-recent" aria-labelledby="discovery-recent-title">
              <div className="section-heading">
                <div><h2 id="discovery-recent-title">Recent activity</h2><p>Durable editorial receipts, shown without the machinery.</p></div>
              </div>
              <ol>
                {overviewActivity.map(({ event, summary }) => (
                  <li key={event.id}><span className="activity-mark" /><strong>{summary}</strong><time>{formatRelative(event.createdAt)}</time></li>
                ))}
              </ol>
            </section>
          ) : null}
        </>
      ) : null}

      {!pageLoading && !error && view === 'diagnostics' ? (
        <div className="discovery-diagnostics">
          <p className="diagnostic-intro">Raw coverage, queue, and event records for investigation. Nothing here implies that an integration is currently running.</p>

          <Disclosure title="Coverage ledger" meta={coverage?.lastExaminedAt ? `Observed ${formatRelative(coverage.lastExaminedAt)}` : 'No observation yet'} open>
            {!coverage ? <p className="muted">Coverage has not been measured yet.</p> : (
              <dl className="diagnostic-ledger">
                {Object.entries(coverage).filter(([key]) => key !== 'profileId').map(([key, value]) => (
                  <div key={key}><dt>{readableKey(key)}</dt><dd>{displayValue(value)}</dd></div>
                ))}
              </dl>
            )}
          </Disclosure>

          <Disclosure title="Editorial work queue" meta={`${work.length} records · ${failures} failed`}>
            <div className="work-counts">
              {workCounts.map(({ status, count }) => <div key={status}><span>{readableKey(status)}</span><strong>{count}</strong></div>)}
            </div>
            {work.length === 0 ? <p className="muted">No work records for this profile.</p> : (
              <ol className="work-list">
                {work.map((item) => (
                  <li key={item.id}>
                    <header><strong>{readableKey(item.kind)}</strong><span className={`chip${item.status === 'failed' ? ' bad' : item.status === 'claimed' ? ' on' : ''}`}>{item.status}</span></header>
                    <p>Available {formatDateTime(item.availableAt)} · attempt {item.attempts}</p>
                    {item.lastError ? <p className="fail">{item.lastError}</p> : null}
                  </li>
                ))}
              </ol>
            )}
          </Disclosure>

          <Disclosure title="All observations" meta={`${observations.length} records`}>
            {observations.length === 0 ? <p className="muted">No observations for this profile.</p> : (
              <div className="observation-list">
                {observations.map((observation) => (
                  <article key={observation.id}>
                    <header><strong>{readableKey(observation.sourceType)} #{observation.sourceId}</strong><time>{formatDateTime(observation.lastExaminedAt)}</time></header>
                    <dl>{Object.entries(observation.judgments).map(([key, value]) => <div key={key}><dt>{readableKey(key)}</dt><dd>{displayValue(value)}</dd></div>)}</dl>
                    <footer><span>{observation.relatedSources.length} related sources</span><span>{observation.nextRevisitAt ? `Revisit ${formatDateTime(observation.nextRevisitAt)}` : 'No revisit scheduled'}</span></footer>
                  </article>
                ))}
              </div>
            )}
          </Disclosure>

          <Disclosure title="Editorial event log" meta={`${activity.length} records`}>
            {activity.length === 0 ? <p className="muted">No editorial events for this profile.</p> : (
              <ol className="editorial-activity diagnostic-events">
                {activity.map((event) => <li key={event.id}><time>{formatDateTime(event.createdAt)}</time><strong>{event.summary}</strong><span>{event.actor}</span></li>)}
              </ol>
            )}
          </Disclosure>
        </div>
      ) : null}
    </div>
  );
}
